/**
 * Turn whatever an admin pastes into the Map field into an embeddable Google
 * Maps iframe URL — so they don't need to know about "embed URLs".
 *
 * Accepts, in order:
 *   1. A real Google Maps embed URL (Maps → Share → "Embed a map"): used as-is.
 *   2. A normal Google Maps link (share/place/search/@lat,lng): we pull out the
 *      place/coords and build a keyless embed (`maps.google.com/maps?q=…&output=embed`).
 *   3. Plain text (an address or place name): treated as the query for the same
 *      keyless embed.
 *
 * The keyless `?q=…&output=embed` form needs no API key and iframes fine (unlike
 * a normal maps link, which Google blocks with X-Frame-Options). Only https
 * Google hosts are emitted, matching the CSP frame-src allowlist.
 *
 * Returns an iframe src, or null if we can't build one (empty input).
 */
export function mapEmbedUrl(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim()
  if (!raw) return null

  // Not a URL → treat the whole thing as an address/place query.
  if (!/^https?:\/\//i.test(raw)) {
    return buildEmbed(raw)
  }

  let u: URL
  try {
    u = new URL(raw)
  } catch {
    return buildEmbed(raw)
  }

  const host = u.hostname.replace(/^www\./, '')
  const isGoogle =
    host === 'google.com' ||
    host === 'maps.google.com' ||
    host.endsWith('.google.com')

  // 1. Already an embed URL (from "Embed a map") — use directly.
  if (isGoogle && u.protocol === 'https:') {
    if (u.pathname.startsWith('/maps/embed')) return u.toString()
    if (u.searchParams.get('output') === 'embed') return u.toString()
  }

  // 2. A normal Google Maps link — extract a query and build a keyless embed.
  if (isGoogle) {
    const q =
      u.searchParams.get('q') ||
      u.searchParams.get('query') ||
      placeFromPath(u.pathname) ||
      coordsFromAt(u.pathname)
    if (q) return buildEmbed(q)
  }

  // 3. Non-Google links (e.g. a short goo.gl/maps link) can't be embedded
  //    keylessly — fall back to a button (null).
  return null
}

/** Extract the place name from a `/maps/place/<Name>/…` path. */
function placeFromPath(pathname: string): string | null {
  const m = pathname.match(/\/maps\/place\/([^/]+)/)
  if (!m) return null
  try {
    return decodeURIComponent(m[1].replace(/\+/g, ' '))
  } catch {
    return m[1].replace(/\+/g, ' ')
  }
}

/** Extract `lat,lng` from a `…/@37.42,-122.08,15z` path. */
function coordsFromAt(pathname: string): string | null {
  const m = pathname.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
  return m ? `${m[1]},${m[2]}` : null
}

/** Keyless embeddable map for an address/place/coords query. */
function buildEmbed(query: string): string {
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
}
